import React from 'react';
import { observer } from 'mobx-react';
import { useRouter } from 'next/compat/router';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { Header } from 'src/dispensary/core-menu/components/header';
import { ConciergeFab } from 'src/dispensary/core-menu/components/concierge-fab';
import { KioskProLayout } from 'src/components/kiosk-pro/kiosk-pro-layout';
import CoreMenuFooter from 'dispensary/core-menu/components/core-menu-footer';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'src/hooks/use-ui';
import { useOrderTypeTakeoverPrompt } from 'src/hooks/use-order-type-takeover-prompt';
import { WhoopsContent, WhoopsReason } from 'src/components/whoops';

import { LayoutWrapper } from './layout-wrapper';

type CoreMenuLayoutProps = {
  children: React.ReactNode;
};

export const CoreMenuLayout = observer(({ children }: CoreMenuLayoutProps): JSX.Element | null => {
  const { dispensary, loading, error } = useDispensary();
  const router = useRouter();
  const isKioskProEnabled = useKioskPro();
  const { onCollectionsPage, isKiosk, isStoreFront } = useUI();
  const isConciergeFabEnabled = Boolean(dispensary?.storeSettings?.enableConcierge) && isStoreFront;

  useOrderTypeTakeoverPrompt();

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- boolean expression */
  const hideCategories =
    isKioskProEnabled || !(router?.route.includes('products') || router?.route.includes('brands') || onCollectionsPage);
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
  const hideFooter = isKioskProEnabled && isKiosk;

  if (isKioskProEnabled && isKiosk && dispensary) {
    return (
      <KioskProLayout dispensary={dispensary} hideCategories={hideCategories} hideFooter={hideFooter}>
        {children}
      </KioskProLayout>
    );
  }

  if (error) {
    console.error('Error fetching dispensary', error);
  }

  if (!loading && !dispensary && !error) {
    console.error('Dispensary not found', error);
    return <WhoopsContent reason={WhoopsReason.pageNotFound} />;
  }

  return (
    <LayoutWrapper>
      <Header dispensary={dispensary} />

      {isConciergeFabEnabled && <ConciergeFab cName={dispensary?.cName ?? null} />}

      {children}

      <CoreMenuFooter hideCategories={hideCategories} />
    </LayoutWrapper>
  );
});
